/**
 * Форма заполнения переменных WABA шаблона (TODO-027a)
 * Дата: 15.01.2026
 *
 * СВЯЗАННЫЕ МОДУЛИ И ФАЙЛЫ:
 * - `frontend/src/api/waba.ts` — отправка шаблона (sendTemplate)
 * - `frontend/src/components/features/messages/ChatWindow.tsx` — открывает/закрывает форму
 *
 * КАК РАБОТАЕТ (кратко):
 * - Показывает превью BODY текста и inputs для переменных {{1}}, {{2}}, ...
 * - На submit вызывает backend `/api/messages/send-template`
 *
 * СХЕМА:
 * - Композиция функций:
 *   - WABATemplateForm -> buildPreview(values) -> wabaApi.sendTemplate -> invalidate queries
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import type { WABATemplate } from '@/api/templates';
import { wabaApi } from '@/api/waba';
import { useToast } from '@/hooks/useToast';
import { Button } from '@/components/ui';
import { cn } from '@/utils/cn';

interface WABATemplateFormProps {
  isOpen: boolean;
  tenantId?: string;
  phone: string;
  template: WABATemplate | null;
  patientName?: string;
  onClose: () => void;
  onSent?: () => void;
}

function buildPreview(text: string, values: Record<string, string>) {
  return text.replace(/\{\{\s*(\d+)\s*\}\}/g, (_, idx) => values[idx] ?? `{{${idx}}}`);
}

export function WABATemplateForm({ isOpen, tenantId, phone, template, patientName, onClose, onSent }: WABATemplateFormProps) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const vars = useMemo(() => {
    const v = template?.variables || [];
    const uniq = Array.from(new Set(v));
    uniq.sort((a, b) => Number(a) - Number(b));
    return uniq;
  }, [template]);

  const initialValues = useMemo(() => {
    const base: Record<string, string> = {};
    for (const v of vars) base[v] = '';
    if (vars.includes('1') && patientName) base['1'] = patientName;
    return base;
  }, [patientName, vars]);

  const [values, setValues] = useState<Record<string, string>>({});

  // синхронизируем values при открытии/смене шаблона
  useEffect(() => {
    if (isOpen) setValues(initialValues);
  }, [initialValues, isOpen, template?.template_guid]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!template?.template_guid) throw new Error('template_guid required');
      return wabaApi.sendTemplate({
        tenant_id: tenantId,
        phone,
        template_guid: template.template_guid,
        template_name: template.name,
        variables: values,
      });
    },
    onSuccess: () => {
      toast.showSuccess('Шаблон отправлен');
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      onSent?.();
      onClose();
    },
    onError: (e: any) => {
      toast.showError(e?.message || 'Не удалось отправить шаблон');
    },
  });

  const body = template?.body_text || template?.components?.find((c) => c.type === 'BODY')?.text || '';
  const preview = buildPreview(body || '', values || {});

  if (!isOpen || !template) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="waba-template-form">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className={cn('relative w-[720px] max-w-full rounded-lg bg-white border border-neutral-200 shadow-soft')}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <div className="text-sm font-semibold text-neutral-900">
            Отправить шаблон: {template.name}
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-neutral-100 transition-colors" aria-label="Close">
            <X className="w-4 h-4 text-neutral-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <div className="text-xs font-medium text-neutral-600 mb-2">Превью</div>
            <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-800 whitespace-pre-wrap">
              {preview || '—'}
            </div>
          </div>

          {vars.length > 0 && (
            <div>
              <div className="text-xs font-medium text-neutral-600 mb-2">Переменные</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {vars.map((v) => (
                  <label key={v} className="text-xs text-neutral-600">
                    {`{{${v}}}`}
                    <input
                      value={values[v] ?? ''}
                      onChange={(e) => setValues((prev) => ({ ...prev, [v]: e.target.value }))}
                      className="mt-1 input w-full"
                      placeholder="Введите значение"
                      data-testid={`waba-template-var-${v}`}
                    />
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-neutral-200 flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending} testId="waba-template-cancel">
            Отмена
          </Button>
          <Button onClick={() => mutation.mutate()} isLoading={mutation.isPending} testId="waba-template-send">
            Отправить шаблон
          </Button>
        </div>
      </div>
    </div>
  );
}

